import React, { useEffect } from 'react';
import { Button, Divider, Dropdown, Table, TableColumnsType, TableProps } from 'antd';
import { EllipsisOutlined,
  ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../Layout/context';
import { useSearchParams } from '../../../hooks/useSearchParams';
import { useSimpleModal } from '../../Common/Modal/Simple';
import { getValidSearchParams } from '../../../utils';
import ContentHeader from '../../Common/ContentHeader';
import { useMessageError } from '../../../hooks/common';
import ButtonEye from './ButtonEye';
import { InternationalOrderColumn,
  InternationalResponse,
  useInternationalDelete,
  useInternationalGet } from '../../../hooks/api/internationals';
import Comments from '../Comments';
import InputSearch from '../../Common/InputSearch';

import styles from '../Tickets/index.module.scss';

export default function Pages(): JSX.Element {
  const { setBreadcrumbs } = useLayoutContext();
  const navigate = useNavigate();
  const internationalGet = useInternationalGet();
  const internationalDelete = useInternationalDelete();
  const { searchParams, replaceSearchParams } = useSearchParams();
  const { modal, contextHolder } = useSimpleModal();
  const fetch = () => {
    internationalGet.fetch({
      page: 1,
      pageSize: 10,
      ...getValidSearchParams(
        (keys) => keys.filter((key) => key.indexOf('comment_') !== 0),
        searchParams,
      ),
    });
  };

  useMessageError([internationalGet, internationalDelete]);

  useEffect(() => {
    setBreadcrumbs([{ title: 'Internationals' }]);
  }, []);

  useEffect(fetch, [searchParams, internationalDelete.data]);

  const onChange: TableProps<InternationalResponse>['onChange'] = (
    pagination,
    _,
    sorter,
  ) => {
    replaceSearchParams({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      orderByColumn: (Array.isArray(sorter) ? sorter[0].field : sorter.field) as InternationalOrderColumn,
      // eslint-disable-next-line no-nested-ternary
      orderBy: (Array.isArray(sorter) ? sorter[0].order : sorter.order)
        ? ((Array.isArray(sorter) ? sorter[0].order : sorter.order) === 'ascend'
          ? 'ASC'
          : 'DESC')
        : '',
    });
  };

  const confirmDelete = (internationalId: string) => {
    if (internationalId) {
      modal.confirm({
        title: 'Are you sure you want to Delete the page?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => internationalDelete.fetch(internationalId),
        okText: 'Delete',
      });
    }
  };

  const columns: TableColumnsType<InternationalResponse> = [
    {
      title: 'Country',
      dataIndex: 'country.id',
      sorter: true,
      render: (_, international) => international.country.name,
    },
    {
      title: 'Quantity of Users',
      dataIndex: 'userCount',
      // sorter: true,
    },
    {
      title: 'Actions',
      width: 50,
      dataIndex: 'actions',
      render: (_, international) => (
        <div className={styles.actions}>
          <ButtonEye international={international} onChange={fetch} loading={internationalGet.loading} />

          <Dropdown
            menu={{
              items: [
                {
                  key: '1',
                  label: 'Edit',
                  onClick: () => {
                    navigate(`/internationals/${international.id}`);
                  },
                },
                {
                  key: '2',
                  label: 'Delete',
                  onClick: () => confirmDelete(international.id),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <ContentHeader title="International list">
        <InputSearch />
        <Button
          type="primary"
          onClick={(e) => {
            e.preventDefault();
            navigate('create');
          }}
        >
          Create
        </Button>
      </ContentHeader>
      {contextHolder}

      <div style={{ minHeight: 'calc(100vh - 128px)' }}>
        <Table<InternationalResponse>
          columns={columns}
          rowKey="id"
          dataSource={internationalGet.data?.data || []}
          onChange={onChange}
          pagination={{
            current: +(searchParams.get('page') || 1) || 1,
            pageSize: +(searchParams.get('pageSize') || 10) || 10,
            total: internationalGet.data?.meta.itemCount || 0,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} item${total > 1 ? 's' : ''}`,
          }}
        />

        <div>
          <Divider orientation="left" plain orientationMargin={16} className="divider">
            <span className="dividerText">Comments</span>
          </Divider>

          <Comments
            loading={internationalGet.loading}
            hideTitle
            filter={{ entity: 'international_community' }}
          />
        </div>
      </div>
    </>
  );
}
